import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import {
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { AccountClient } from 'AccountClient'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { calculateDocAmt } from 'containers/ARAPModule/Helper/AmountCalculation'
import { isBefore } from 'date-fns'
import { useGetTaxSchemeNewListingQuery } from 'generated/graphql'
import { calculateTaxDetail } from 'helpers/Calculation/taxCalculationDetail'
import { filterTaxSchemeEffectiveDate } from 'helpers/Filter/filterTaxSchemeEffectiveDate'
import { formatDashDate } from 'helpers/StringNumberFunction/FormatDate'
import { amtStr } from 'helpers/StringNumberFunction/NumFormatters'
import { CommonYupValidation } from 'helpers/YupSchema/yup'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import * as yup from 'yup'

interface ScheduledBillingItemProps {
  DebtorAccountID: string
  CompanyID: string
  SalesOrderID: string
  BillingDate: Date
  BillingAmt: number
  BillItemID: string
  TaxSchemeID: string
  TaxAmt: number
  DocAmt: number
  Description: string
  Remark: string
  TaxAmtDetail: any
}

export const ARScheduledBillingItemComponent = ({
  openItemDialog,
  setOpenItemDialog,
  itemData,
  BillItemLoading,
  fetchFilteredBillItem,
  CompanyID,
  CostCentreID,
  getBillItem,
  isEdit,
  menu,
}) => {
  const editData: any = menu?.obj
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  let form, mode
  switch (isEdit) {
    case 'add':
      form = 'New'
      mode = 'add'
      break
    case 'edit':
      form = 'Draft'
      mode = 'edit'
      break
  }

  /* ------------------------------------------- */
  /*                   STATE                     */
  /* ------------------------------------------- */
  const [mountDefVal, setMountDefVal] = useState(!!editData)
  const [billingAmt, setBillingAmt] = useState(editData?.BillingAmt ?? 0)
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)
  const [selectedTax, setSelectedTax] = useState(null)
  const [taxAmt, setTaxAmt] = useState(
    !!menu?.obj?.TaxAmt ? menu?.obj?.TaxAmt : 0
  )

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const ItemSchema = yup.object().shape({
    BillItemID: CommonYupValidation.requireField('Item is required'),
    BillingAmt: yup
      .number()
      .transform((originalValue, originalObject) => {
        if (originalValue?.length > 3) {
          return parseInt(originalObject.replace(/,/g, ''))
        } else {
          return parseFloat(originalObject)
        }
      })
      .min(0.0001, 'Billing Amount must be at least 0.0001')
      .required('Billing Amount is required'),
    TaxSchemeID: CommonYupValidation.requireField('Tax Code is required'),
    BillingDate: yup
      .date()
      .required('Billing Date is required')
      .test(
        'end-date-test',
        `Billing date cannot be less than today's date`,
        function(value) {
          if (!value) return true
          const selectedDate = new Date(value)
          selectedDate.setHours(0, 0, 0, 0)

          const today = new Date()
          today.setHours(0, 0, 0, 0)

          return !isBefore(selectedDate, today)
        }
      ),
    Description: CommonYupValidation.requireField('Description is required'),
  })

  const {
    handleSubmit: handleItemSubmit,
    register: itemRegister,
    setValue: setValueItem,
    watch: itemWatch,
    errors: itemErrors,
    control: itemControl,
    clearErrors: clearErrorsItem,
    reset: itemReset,
  } = useForm<ScheduledBillingItemProps>({
    ...(mountDefVal && {
      defaultValues: {
        BillItemID: !!menu?.obj?.BillItemID ? menu?.obj?.BillItemID : null,
        BillingDate: !!menu?.obj?.BillingDate
          ? menu?.obj?.BillingDate
          : new Date(),
        BillingAmt: !!menu?.obj?.BillingAmt ? menu?.obj?.BillingAmt : 0,
        TaxSchemeID: !!menu?.obj?.TaxSchemeID ? menu?.obj?.TaxSchemeID : null,
        TaxAmt: !!menu?.obj?.TaxAmt ? menu?.obj?.TaxAmt : 0,
        Description: !!menu?.obj?.Description ? menu?.obj?.Description : '',
        Remark: !!menu?.obj?.Remark ? menu?.obj?.Remark : '',
      },
    }),
    mode: 'onSubmit',
    resolver: yupResolver(ItemSchema),
  })

  /* -------------------------------------------- */
  /*                    QUEARY                    */
  /* -------------------------------------------- */
  const {
    loading: taxSchemeLoading,
    data: { getTaxSchemeListing } = { getTaxSchemeListing: [] },
  } = useGetTaxSchemeNewListingQuery({
    fetchPolicy: 'network-only',
    client: AccountClient,
    variables: { TaxClass: 'OUTPUT' },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      if (menu?.obj?.TaxSchemeID) {
        let filteredTaxScheme = filterTaxSchemeEffectiveDate(
          menu?.obj?.BillingDate,
          getTaxSchemeListing
        )

        let selectTax = filteredTaxScheme.find(
          x => x?.id === menu?.obj?.TaxSchemeID
        )
        setSelectedTax(selectTax)
        setValueItem('TaxSchemeID', selectTax?.id)
        clearErrorsItem('TaxSchemeID')

        initializeTaxDetailsInEditMode(selectTax)

        let recalculateCost =
          selectTax?.isExclusive === true
            ? menu?.obj?.DocAmt - menu?.obj?.TaxAmt
            : menu?.obj?.DocAmt

        setBillingAmt(recalculateCost)
        setValueItem('BillingAmt', recalculateCost)
      }
    },
  })

  const taxSchemeOutput = filterTaxSchemeEffectiveDate(
    itemWatch('BillingDate'),
    getTaxSchemeListing
  )

  /* -------------------------------------------- */
  /*                    VARIABLE                    */
  /* -------------------------------------------- */

  const defaultVal: any = {
    BillingDate: new Date(),
    BillItemID: '',
    BillingAmt: 0,
    TaxSchemeID: '',
    TaxAmt: 0,
    TaxRate: 0,
    Description: '',
    Remark: '',
  }

  const resetItem = () => {
    itemReset(defaultVal)
    setBillingAmt(0)
  }

  const taxCodeName = getTaxSchemeListing?.find(x => x?.id === selectedTax?.id)
    ?.name

  const itemName =
    itemWatch('BillItemID') !== undefined
      ? getBillItem.find(x => x?.BillItemID === itemWatch('BillItemID'))?.Name
      : getBillItem.find(x => x?.BillItemID === menu?.obj?.BillItemID)?.Name

  /* -------------------------------------------- */
  /*                  ON SUBMIT                   */
  /* -------------------------------------------- */
  const onSubmit = (data, docAmt, index, isSaveNew) => {
    const formattedTaxDetails = result.taxDetails.map((tax, idx) => {
      return {
        seqNo: tax?.SeqNo,
        taxAmt: itemWatch(`TaxAmtDetail[${idx}]`) ?? tax.TaxAmount,
        taxRate: tax.TaxRate * 100,
        taxCode: tax.taxCode,
        taxId: tax.taxId,
        taxSchemeDetailId: tax.taxSchemeDetailId,
        taxSchemeId: tax.taxSchemeId,
      }
    })

    if (!menu?.obj) {
      itemData.push({
        Sequence: itemData?.length + 1,
        BillItemID: data?.BillItemID,
        BillingDate: formatDashDate(new Date(data?.BillingDate)?.toISOString()),
        BillingAmt: Number(docAmt) - Number(taxAmt),
        TaxSchemeID: data?.TaxSchemeID,
        TaxCode: taxCodeName,
        TaxRate: 0,
        BillItemName: itemName,
        TaxAmt: taxAmt,
        DocAmt: Number(docAmt),
        Description: data?.Description,
        Remark: data?.Remark,
        ItemTaxInfo: formattedTaxDetails,
      })
    } else {
      itemData[index] = {
        Sequence: itemData?.Sequence ?? index + 1,
        ScheduledBillingItemID: itemData[index]?.ScheduledBillingItemID,
        BillItemID: data?.BillItemID,
        BillingDate: formatDashDate(new Date(data?.BillingDate)?.toISOString()),
        BillingAmt: Number(docAmt) - Number(taxAmt),
        BillItemName: itemName,
        TaxRate: 0,
        TaxCode: taxCodeName,
        TaxSchemeID: data?.TaxSchemeID,
        TaxAmt: taxAmt,
        DocAmt: Number(docAmt),
        Description: data?.Description,
        Remark: data?.Remark,
        ItemTaxInfo: formattedTaxDetails,
      }
    }
    resetItem()
    if (!isSaveNew) {
      setOpenItemDialog(false)
    } else {
      fetchFilteredBillItem({ variables: { CompanyID, CostCentreID } })
      setOpenItemDialog(true)
    }
  }

  const handleUnitPriceChange = event => {
    setBillingAmt(event.floatValue)
    setValueItem('BillingAmt', event?.floatValue)

    if (selectedTax) {
      prepareTaxCalculationData(selectedTax)
      const taxResult = calculateTaxFromState(selectedTax, event.value)
      setTaxAmt(taxResult?.TaxAmount)
      updateTaxDetailsInForm(taxResult?.taxDetails, taxResult?.TaxAmount)
      setValueItem(
        'TaxAmt',
        calculateTaxFromState(selectedTax, event.value)?.TaxAmount
      )
    }
  }

  // ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ - TAX CALCULATION - ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ //
  const prepareTaxCalculationData = (selectedTax: any) => {
    if (!selectedTax?.taxSchemeDetailList[0]?.taxSchemeDetailItemList?.length) {
      return []
    }

    const taxSchemeDetail = selectedTax?.taxSchemeDetailList[0]

    return taxSchemeDetail.taxSchemeDetailItemList.map(item => ({
      taxId: item.taxId,
      taxSchemeId: taxSchemeDetail.taxSchemeId,
      taxSchemeDetailId: taxSchemeDetail.id,
      seqNo: item.seqNo,
      taxOnSeq: item.taxOnSeq || [],
      computeMethod:
        item.computeMethod === 'TAX_ON_GROSS'
          ? 'Tax_On_Gross'
          : 'Tax_On_Taxed_Price',
      taxRate: item.taxRate,
      taxCode: item.taxInfo?.code,
    }))
  }

  const calculateTaxFromState = (selectedTax: any, amount: number) => {
    const taxData = prepareTaxCalculationData(selectedTax)

    const isExclusive = Boolean(selectedTax?.isExclusive)

    return calculateTaxDetail(taxData, amount, isExclusive)
  }
  let result = calculateTaxFromState(selectedTax, billingAmt)

  const initializeTaxDetailsInEditMode = selectTax => {
    if (menu?.obj?.TaxAmtDetails && Array.isArray(menu?.obj?.TaxAmtDetails)) {
      menu.obj.TaxAmtDetails.forEach((taxDetail, index) => {
        itemRegister(`TaxAmtDetail[${index}]`)
        setValueItem(`TaxAmtDetail[${index}]`, taxDetail.taxAmount)
      })
      setValueItem('taxDetailsCount', menu.obj.TaxAmtDetails.length)
    } else if (selectTax) {
      const currentAmount = Number(
        selectTax?.isExclusive === true
          ? menu?.obj?.BillingAmt
          : menu?.obj?.DocAmt
      )
      const taxResult = calculateTaxFromState(selectTax, currentAmount)
      if (taxResult?.taxDetails) {
        updateTaxDetailsInForm(taxResult.taxDetails, taxResult.TaxAmount)
      }
    }
  }

  const updateTaxDetailsInForm = (taxDetails, taxAmount) => {
    if (!taxDetails || taxDetails.length === 0) return
    const prevTaxDetailsCount = itemWatch('taxDetailsCount') || 0
    taxDetails.forEach((tax, index) => {
      itemRegister(`TaxAmtDetail[${index}]`)
      setValueItem(`TaxAmtDetail[${index}]`, tax.TaxAmount)
    })
    setValueItem('taxDetailsCount', taxDetails.length)
    if (prevTaxDetailsCount > taxDetails.length) {
      for (let i = taxDetails.length; i < prevTaxDetailsCount; i++) {
        setValueItem(`TaxAmtDetail[${i}]`, null)
      }
    }
    setValueItem('TaxAmt', taxAmount)
    setTaxAmt(taxAmount)
  }

  const docAmt = selectedTax?.isExclusive
    ? calculateDocAmt(billingAmt, taxAmt)
    : Number(billingAmt)

  const handleTaxChange = e => {
    const currentTax = taxSchemeOutput.find(x => x?.id === e)
    const taxResult = calculateTaxFromState(currentTax, billingAmt)
    updateTaxDetailsInForm(taxResult?.taxDetails, taxResult?.TaxAmount)
    setSelectedTax(taxSchemeOutput?.find(x => x?.id === e))
    setTaxAmt(taxResult?.TaxAmount)
  }

  // Reset item when dialog closes
  useEffect(() => {
    if (!openItemDialog) {
      resetItem()
    }
  }, [openItemDialog])
  return (
    <>
      {BillItemLoading && <Loading />}
      {taxSchemeLoading && <Loading />}
      <CommonDialog
        fullWidth={true}
        open={openItemDialog}
        onClose={() => {
          setOpenItemDialog(false)
        }}
        sections={{
          header: {
            title: 'Billing Schedule',
            rightText: !!menu?.obj ? 'Edit' : 'New',
          },
          body: () => (
            <div className="content-container" style={{ marginTop: '-20px' }}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Controller
                  as={KeyboardDatePicker}
                  name="BillingDate"
                  required
                  label="Schedule Billing Date"
                  control={itemControl}
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  ref={itemRegister}
                  KeyboardButtonProps={{ 'aria-label': 'change date' }}
                  showTodayButton
                  className="left"
                  value={new Date()}
                  defaultValue={editData ? editData?.BillingDate : new Date()}
                  helperText={itemErrors?.BillingDate?.message}
                  error={itemErrors?.BillingDate ? true : false}
                  onChange={(e: Date | null) => {}}
                />
              </MuiPickersUtilsProvider>

              <Controller
                name="BillingAmt"
                control={itemControl}
                defaultValue={billingAmt}
                rules={{ required: true }}
                className="right"
                render={({ value }) => (
                  <NumberFormat
                    {...value}
                    value={billingAmt}
                    onValueChange={e => handleUnitPriceChange(e)}
                    customInput={TextField}
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                    margin="dense"
                    label="Billing Amount"
                    id="standard-basic"
                    className="right"
                    required
                    helperText={itemErrors?.BillingAmt?.message}
                    error={itemErrors?.BillingAmt ? true : false}
                  />
                )}
                helperText={itemErrors?.BillingAmt?.message}
                error={itemErrors?.BillingAmt ? true : false}
              />

              <Controller
                render={({ onChange, onBlur, value }) => {
                  const val = getBillItem?.find(
                    x => x?.BillItemID === itemWatch('BillItemID')
                  )

                  return (
                    <Autocomplete
                      options={
                        getBillItem?.sort((a, b) => {
                          return a.Name.localeCompare(b.Name)
                        }) || []
                      }
                      getOptionLabel={option =>
                        `${option?.Name} | ${option?.Description}`
                      }
                      fullWidth
                      onChange={(value, newValue: any, reason) => {
                        setValueItem('BillItemID', newValue?.BillItemID)
                        setMountDefVal(false)
                        if (newValue?.TaxSchemeID) {
                          itemRegister('TaxSchemeID')
                          setValueItem('TaxSchemeID', newValue?.TaxSchemeID)
                          handleTaxChange(newValue?.TaxSchemeID)
                        }
                      }}
                      value={val}
                      renderOption={(props, option) => {
                        return (
                          <div>
                            <div>
                              <span className="xsTitle">{props?.Name}</span>
                            </div>
                            <div className="desc">{props?.Description}</div>
                          </div>
                        )
                      }}
                      renderInput={(params: any) => {
                        return (
                          <div>
                            <TextField
                              {...params}
                              required
                              label="Billing Item"
                              variant="standard"
                              fullWidth
                              name="BillItemID"
                              helperText={itemErrors?.BillItemID?.message}
                              error={itemErrors?.BillItemID ? true : false}
                            />
                          </div>
                        )
                      }}
                    />
                  )
                }}
                name="BillItemID"
                autoComplete="off"
                control={itemControl}
                multiline={true}
                fullWidth
                margin="dense"
                ref={itemRegister}
                required
                helperText={itemErrors?.BillItemID?.message}
                error={itemErrors?.BillItemID ? true : false}
              />

              <Controller
                as={
                  <TextField
                    label="Tax Scheme"
                    required
                    fullWidth
                    SelectProps={{
                      renderValue: () =>
                        `${selectedTax?.name} | ${selectedTax?.description} ${
                          selectedTax?.isExclusive ? `(E)` : `(I)`
                        }`,
                      onChange: event => {
                        handleTaxChange(event?.target?.value)
                        setValueItem(
                          'TaxSchemeID',
                          event?.target?.value.toString()
                        )
                        clearErrorsItem('TaxSchemeID')
                        setMountDefVal(false)
                      },
                    }}
                    select
                    helperText={itemErrors?.TaxSchemeID?.message}
                    error={itemErrors?.TaxSchemeID ? true : false}
                  >
                    <ListSubheader>Output</ListSubheader>
                    {taxSchemeOutput?.map((taxScheme, index) => (
                      <MenuItem
                        id="tax-select"
                        key={index}
                        value={taxScheme?.id}
                      >
                        {
                          <div className="content-wrap full">
                            <div>
                              <span className="xsTitle">{taxScheme?.name}</span>
                            </div>
                            <div className="desc">
                              {taxScheme?.description}{' '}
                              {taxScheme?.isExclusive ? `(E)` : `(I)`}
                            </div>
                          </div>
                        }
                      </MenuItem>
                    ))}
                  </TextField>
                }
                select
                name="TaxSchemeID"
                autoComplete="off"
                control={itemControl}
                defaultValue={menu?.obj?.TaxSchemeID || ''}
                ref={itemRegister}
              />

              {result.taxDetails.length > 0 && (
                <Grid item xs={12}>
                  <Paper
                    elevation={1}
                    style={{
                      padding: '1px 7px',
                      borderRadius: '2px',
                      backgroundColor: '#fff9f2',
                    }}
                  >
                    {result.taxDetails.map((tax, index) => (
                      <Grid
                        container
                        key={index}
                        spacing={1}
                        alignItems="center"
                      >
                        <Grid item xs={4}>
                          <Typography
                            variant="body2"
                            style={{
                              fontSize: '0.63rem',
                              fontWeight: 500,
                              lineHeight: '24px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {tax?.taxCode || `Tax ${index + 1}`}
                          </Typography>
                        </Grid>

                        <Grid item xs={3}>
                          <Typography
                            variant="body2"
                            style={{
                              fontSize: '0.63rem',
                              fontWeight: 500,
                              lineHeight: '24px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {typeof tax?.TaxRate === 'number'
                              ? `${(tax.TaxRate * 100).toFixed(2)}%`
                              : `N/A`}
                          </Typography>
                        </Grid>

                        <Grid item xs={5}>
                          <Controller
                            name={`TaxAmtDetail[${index}]`}
                            control={itemControl}
                            defaultValue={tax.TaxAmount}
                            render={({ onChange, value }) => (
                              <NumberFormat
                                customInput={TextField}
                                fullWidth
                                className="disabled-input"
                                thousandSeparator
                                decimalScale={2}
                                fixedDecimalScale
                                style={{
                                  marginTop: '10px',
                                  fontSize: '0.63rem',
                                }}
                                allowNegative={false}
                                size="small"
                                value={
                                  itemWatch(`TaxAmtDetail[${index}]`) ??
                                  tax.TaxAmount
                                }
                                InputProps={{
                                  disableUnderline: true,
                                  readOnly: true,
                                  style: {
                                    fontSize: '0.63rem',
                                    height: '23px',
                                    padding: '0 4px',
                                  },
                                }}
                                InputLabelProps={{ style: { display: 'none' } }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    ))}
                  </Paper>
                </Grid>
              )}

              <Controller
                as={<NumberFormat allowNegative={false} />}
                thousandSeparator
                customInput={TextField}
                ref={itemRegister()}
                control={itemControl}
                margin="dense"
                name="TaxAmt"
                label="Tax Amount"
                onValueChange={e => {
                  setTaxAmt(e?.floatValue)
                }}
                value={taxAmt}
                defaultValue={menu?.obj?.TaxAmt ?? taxAmt ?? 0}
                decimalScale={2}
                fixedDecimalScale
                className="left"
                disabled
                InputProps={{ disableUnderline: true, readOnly: true }}
              />

              <Controller
                render={({ value, onChange }) => {
                  value = { docAmt }
                  return (
                    <TextField
                      margin="dense"
                      label="Doc Amount"
                      value={
                        selectedTax?.isExclusive
                          ? amtStr(calculateDocAmt(billingAmt, taxAmt))
                          : amtStr(billingAmt)
                      }
                      InputProps={{ readOnly: true, disableUnderline: true }}
                      className="right"
                      disabled
                    />
                  )
                }}
                disabled
                margin="dense"
                defaultValue={(docAmt || 0) ?? (menu?.obj?.DocAmt || 0)}
                name="DocAmt"
                ref={itemRegister()}
                control={itemControl}
              />

              <Controller
                as={TextField}
                id="standard-basic"
                name="Description"
                label="Billing Description"
                defaultValue={editData ? editData?.Description : ''}
                autoComplete="off"
                control={itemControl}
                fullWidth
                margin="normal"
                ref={itemRegister}
                required
                helperText={itemErrors?.Description?.message}
                error={itemErrors?.Description ? true : false}
                multiline
              />

              <Controller
                as={TextField}
                id="standard-basic"
                name="Remark"
                label="Billing Remark"
                defaultValue={editData ? editData?.Remark : ''}
                autoComplete="off"
                control={itemControl}
                fullWidth
                margin="normal"
                ref={itemRegister}
                multiline
              />
            </div>
          ),
          footer: {
            actions: !menu?.obj
              ? [
                  {
                    displayText: 'Close',
                    props: {
                      onClick: () => {
                        setOpenItemDialog(false)
                        resetItem()
                      },
                      variant: 'contained',
                      color: 'primary',
                    },
                  },
                  {
                    displayText: 'Confirm & New',
                    props: {
                      onClick: () => {
                        handleItemSubmit(data =>
                          onSubmit(data, docAmt, menu?.index, true)
                        )()
                        setMountDefVal(false)
                      },
                      variant: 'contained',
                      color: 'primary',
                    },
                  },
                  {
                    displayText: 'Confirm',
                    props: {
                      onClick: () => {
                        handleItemSubmit(data =>
                          onSubmit(data, docAmt, menu?.index, false)
                        )()
                      },
                      variant: 'contained',
                      color: 'primary',
                    },
                  },
                ]
              : [
                  {
                    displayText: 'Close',
                    props: {
                      onClick: () => {
                        setOpenItemDialog(false)
                        resetItem()
                      },
                      variant: 'contained',
                      color: 'primary',
                    },
                  },
                  {
                    displayText: 'Confirm',
                    props: {
                      onClick: () => {
                        handleItemSubmit(data =>
                          onSubmit(data, docAmt, menu?.index, false)
                        )()
                      },
                      variant: 'contained',
                      color: 'primary',
                    },
                  },
                ],
          },
        }}
      />

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />
    </>
  )
}
